import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  clearFormLanguageRestriction,
  clearFormLanguageRestrictionOnly,
  setFormLanguageRestriction
} from 'app/redux/actions/FormRestrictionsActions'
import { setUserData } from 'app/redux/actions/UserActions'
import { ReduxUser } from 'app/redux/ReduxInterfaces'
import { isNil } from 'lodash'

interface UseFormLanguageRestrictionResult {
  /** Whether the unsupported-language warning dialog should be shown */
  showLangWarning: boolean
  /** Call when the user confirms the warning — switches the page language to the first supported language */
  switchToSupportedLanguage: () => void
}

/**
 * Handles language restriction logic for forms tied to a funding stream.
 *
 * When the user's language is not in `avaliableInLanguages`:
 * - Shows a warning dialog (`showLangWarning`)
 * - On confirm, temporarily switches `user.language` in Redux to the first
 *   supported language (without saving to Salesforce), so Lingui re-renders
 *   the entire page in that language
 * - Restores the original language when the form unmounts
 *
 * Also keeps `allowedLanguages` in Redux in sync so the topbar language
 * dropdown can disable unsupported options while the form is open.
 *
 * The original pre-restriction language is stored globally in Redux (first-write-wins).
 * When any subsequent form loads whose `avaliableInLanguages` includes that original language,
 * the hook restores it proactively — the language follows the restriction chain and snaps back
 * the moment the user reaches a form that supports it, without waiting for unmount.
 *
 * @param avaliableInLanguages - Languages the funding stream supports. Pass undefined while loading.
 * @param autoSwitch - When true, switches to the supported language immediately without showing
 *   the warning dialog. Use when the host page manages the language context itself.
 * @param preventClear - When true, the language is not reset to its pre-form value on unmount;
 *   the caller is responsible for restoring it.
 */
export function useFormLanguageRestriction (
  avaliableInLanguages: string[] | undefined,
  autoSwitch = false,
  preventClear = false
): UseFormLanguageRestrictionResult {
  const user = useSelector((state: { user: ReduxUser }) => state.user)
  const reduxOriginalLanguage = useSelector(
    (state: { formRestrictions?: { originalLanguage: string | null } }) =>
      state.formRestrictions?.originalLanguage ?? null
  )
  const dispatch = useDispatch()
  const [showLangWarning, setShowLangWarning] = useState(false)
  const originalLanguageRef = useRef<string | null>(null)

  const userRef = useRef(user)
  useEffect(() => { userRef.current = user })

  // Ref so the [] cleanup closure always reads the latest Redux value at unmount time.
  const reduxOriginalLanguageRef = useRef<string | null>(reduxOriginalLanguage)
  useEffect(() => { reduxOriginalLanguageRef.current = reduxOriginalLanguage })

  useEffect(() => {
    if (isNil(avaliableInLanguages) || avaliableInLanguages.length === 0) return

    // Proactive restore: if this form supports the original pre-restriction language,
    // switch back immediately and end the restriction chain.
    if (!preventClear && !isNil(reduxOriginalLanguage) && avaliableInLanguages.includes(reduxOriginalLanguage)) {
      if (user.language !== reduxOriginalLanguage) {
        dispatch(setUserData({ ...user, language: reduxOriginalLanguage }))
      }
      dispatch(clearFormLanguageRestriction())
      return
    }

    const needsSwitch = !avaliableInLanguages.includes(user.language)
    // Capture original language in Redux on first switch so destination forms can restore it.
    const capturedOriginal = needsSwitch && isNil(originalLanguageRef.current) ? user.language : undefined
    dispatch(setFormLanguageRestriction(avaliableInLanguages, capturedOriginal))
    if (needsSwitch) {
      // Show popup only on the very first mismatch (no chain active yet).
      // Mid-chain (user already acknowledged once), switch silently.
      if (!autoSwitch && isNil(reduxOriginalLanguage)) {
        setShowLangWarning(true)
      } else {
        if (isNil(originalLanguageRef.current)) {
          originalLanguageRef.current = user.language
        }
        dispatch(setUserData({ ...userRef.current, language: avaliableInLanguages[0] }))
      }
    }

    return () => {
      // Clear only the restriction list; originalLanguage stays in Redux until the lifecycle ends.
      dispatch(clearFormLanguageRestrictionOnly())
      if (!preventClear && !isNil(originalLanguageRef.current)) {
        dispatch(setUserData({ ...userRef.current, language: originalLanguageRef.current }))
        originalLanguageRef.current = null
      }
    }
  }, [avaliableInLanguages])

  // Unmount-only: restore language from Redux if this form didn't switch it locally
  // (e.g. a destination form opened via navigateAfterSubmit that was already in the
  // correct language and never needed to switch).
  useEffect(() => {
    return () => {
      if (!preventClear) {
        const langToRestore = reduxOriginalLanguageRef.current
        // Guard avoids a redundant dispatch when the [avaliableInLanguages] cleanup already restored.
        if (!isNil(langToRestore) && langToRestore !== userRef.current.language) {
          dispatch(setUserData({ ...userRef.current, language: langToRestore }))
        }
        dispatch(clearFormLanguageRestriction())
      }
    }
  }, [])

  const switchToSupportedLanguage = (): void => {
    if (isNil(avaliableInLanguages) || avaliableInLanguages.length === 0) return
    setShowLangWarning(false)
    if (isNil(originalLanguageRef.current)) {
      originalLanguageRef.current = user.language
    }
    dispatch(setUserData({ ...userRef.current, language: avaliableInLanguages[0] }))
  }

  return { showLangWarning, switchToSupportedLanguage }
}
